import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllHotels, getProperties, getMinimumPrice } from '../../services/g2TravelApi';
import {
  HOTEL_SECTION_PLACEHOLDER_LARGE,
  resolveHotelSectionImage,
} from '../../lib/hotelSectionImages';
import { buildHotelDetailPath } from '../../lib/hotelRoutes';
import { useCurrency } from '../../context/CurrencyContext';
import './WeekendDeals.css';

const WeekendDeals = ({ onViewHotel }) => {
  const { formatFrom } = useCurrency();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const carouselRef = useRef(null);

  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get next weekend dates
  const getWeekendDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 5 = Friday
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;
    const friday = new Date(today);
    friday.setDate(today.getDate() + daysUntilFriday);
    const sunday = new Date(friday);
    sunday.setDate(friday.getDate() + 2);

    const formatDate = (date) => {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    return {
      start: formatDate(friday),
      end: formatDate(sunday),
      checkIn: friday.toISOString().split('T')[0],
      checkOut: sunday.toISOString().split('T')[0],
      nights: 2
    };
  };

  const weekendDates = getWeekendDates();

  // Get rating text based on score
  const getRatingText = (score) => {
    if (score >= 9) return 'Wonderful';
    if (score >= 8.5) return 'Excellent';
    if (score >= 8) return 'Very Good';
    if (score >= 7) return 'Good';
    if (score >= 6) return 'Pleasant';
    return 'Fair';
  };

  // Generate random review count
  const getReviewCount = () => {
    const counts = [3510, 4450, 1684, 4359, 2100, 3200, 1800, 2900];
    return counts[Math.floor(Math.random() * counts.length)];
  };

  useEffect(() => {
    let cancelled = false;

    const buildCard = (hotel, index) => {
      const hotelName = hotel.name || hotel.propertyName || hotel.hotelName || 'Hotel';
      const imageUrl = resolveHotelSectionImage(hotel, index, 'large');
      const basePrice = 150 + index * 30;
      const originalPrice = Math.round(basePrice * 1.1);
      const discountedPrice = basePrice;
      const rating = 7.9 + Math.random() * 1.1;
      const roundedRating = Math.round(rating * 10) / 10;

      return {
        id: hotel.id || hotel.propertyId || hotel.code || `hotel-${index}`,
        name: hotelName,
        image: imageUrl,
        city: hotel.cityName || hotel.city || hotel.location || '',
        country: hotel.countryName || hotel.country || 'United Kingdom',
        rating: roundedRating,
        ratingText: getRatingText(roundedRating),
        reviewCount: getReviewCount(),
        originalPrice,
        discountedPrice,
        hasGenius: index % 2 === 0,
        hasLimitedDeal: index === 2,
        hotelData: hotel,
      };
    };

    const fetchHotels = async () => {
      setLoading(true);
      const dates = getWeekendDates();

      try {
        let baseHotels = [];

        // Prefer cached properties list first (usually faster than full hotels payload).
        try {
          const propertiesData = await getProperties();
          if (Array.isArray(propertiesData) && propertiesData.length > 0) {
            baseHotels = propertiesData;
          }
        } catch {
          /* fall through */
        }

        if (baseHotels.length < 8) {
          try {
            const allHotelsResponse = await getAllHotels({});
            if (
              allHotelsResponse?.success &&
              Array.isArray(allHotelsResponse.hotels) &&
              allHotelsResponse.hotels.length > 0
            ) {
              baseHotels = allHotelsResponse.hotels;
            }
          } catch {
            /* keep properties if any */
          }
        }

        if (!Array.isArray(baseHotels) || baseHotels.length === 0) {
          if (!cancelled) {
            setHotels([]);
            setLoading(false);
          }
          return;
        }

        const selectedHotels = baseHotels.slice(0, 8);
        const quickCards = selectedHotels.map((hotel, index) => buildCard(hotel, index));

        if (!cancelled) {
          setHotels(quickCards);
          setLoading(false);
        }

        const priceResults = await Promise.all(
          selectedHotels.map(async (hotel, index) => {
            const hotelId = hotel.id || hotel.propertyId || hotel.code;
            if (!hotelId) return { index, priceData: null };
            try {
              const priceData = await getMinimumPrice(hotelId, dates.checkIn, dates.nights);
              return { index, priceData };
            } catch {
              return { index, priceData: null };
            }
          })
        );

        if (cancelled) return;

        setHotels((prev) => {
          if (!prev.length) return prev;
          const next = prev.map((card) => ({ ...card }));
          priceResults.forEach(({ index, priceData }) => {
            if (!next[index] || !priceData?.minPrice || priceData.minPrice <= 0) return;
            next[index] = {
              ...next[index],
              originalPrice: Math.round(priceData.minPrice * 1.1),
              discountedPrice: Math.round(priceData.minPrice),
            };
          });
          return next;
        });
      } catch {
        if (!cancelled) {
          setHotels([]);
          setLoading(false);
        }
      }
    };

    fetchHotels();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNext = () => {
    const maxIndex = Math.max(0, hotels.length - 4);
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleHotelClick = (hotel) => {
    if (onViewHotel && hotel.id) {
      onViewHotel(hotel.id, hotel.hotelData);
    } else {
      navigate(buildHotelDetailPath(hotel.id));
    }
  };

  // On mobile, show all hotels for horizontal scrolling
  const visibleHotels = isMobile ? hotels : hotels.slice(currentIndex, currentIndex + 4);
  const canGoNext = !isMobile && currentIndex < hotels.length - 4;

  if (loading) {
    return (
      <section className="weekend-deals">
        <div className="weekend-deals__container">
          <div className="weekend-deals__header">
            <h2 className="weekend-deals__title">Deals for the weekend</h2>
            <p className="weekend-deals__subtitle">Loading deals...</p>
          </div>
        </div>
      </section>
    );
  }

  if (hotels.length === 0) {
    return null;
  }

  return (
    <section className="weekend-deals">
      <div className="weekend-deals__container">
        <div className="weekend-deals__header">
          <h2 className="weekend-deals__title">Deals for the weekend</h2>
          <p className="weekend-deals__subtitle">
            Save on stays for {weekendDates.start} - {weekendDates.end}
          </p>
        </div>

        <div className="weekend-deals__wrapper" ref={carouselRef}>
          <div className="weekend-deals__cards">
            {visibleHotels.map((hotel) => (
              <div
                key={hotel.id}
                className="weekend-deals__card"
                onClick={() => handleHotelClick(hotel)}
              >
                <div className="weekend-deals__card-image-wrapper">
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="weekend-deals__card-image"
                    onError={(e) => {
                      e.target.src = HOTEL_SECTION_PLACEHOLDER_LARGE;
                    }}
                  />
                  {hotel.hasGenius && (
                    <div className="weekend-deals__card-genius">Genius</div>
                  )}
                  {hotel.hasLimitedDeal && (
                    <div className="weekend-deals__card-deal">Limited-time Deal</div>
                  )}
                </div>
                <div className="weekend-deals__card-content">
                  <h3 className="weekend-deals__card-name">{hotel.name}</h3>
                  <p className="weekend-deals__card-location">
                    {hotel.city}, {hotel.country}
                  </p>
                  <div className="weekend-deals__card-rating">
                    <span className="weekend-deals__card-rating-score">{hotel.rating}</span>
                    <span className="weekend-deals__card-rating-text">{hotel.ratingText}</span>
                    <span className="weekend-deals__card-reviews">{hotel.reviewCount.toLocaleString()} reviews</span>
                  </div>
                  <div className="weekend-deals__card-price">
                    <span className="weekend-deals__card-price-original">
                      {weekendDates.nights} nights {formatFrom(hotel.originalPrice, 'GBP')}
                    </span>
                    <span className="weekend-deals__card-price-discount">
                      {formatFrom(hotel.discountedPrice, 'GBP')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {canGoNext && (
            <button
              className="weekend-deals__arrow weekend-deals__arrow--right"
              onClick={handleNext}
              aria-label="Next hotels"
            >
              ›
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default WeekendDeals;
